import React, { useEffect, useMemo, useState } from 'react';
import { fmtDate } from '../utils/dateUtils';

const OCC_COLS = [
  { key: 'rate_single', label: 'single', occupancy: 'SINGLE' },
  { key: 'rate_double', label: 'double', occupancy: 'DOUBLE' },
  { key: 'rate_triple', label: 'triple', occupancy: 'TRIPLE' },
  { key: 'rate_quad', label: 'quadruple', occupancy: 'QUAD' },
  { key: 'rate_extra_adult', label: 'extra adult', occupancy: 'EXTRA_ADULT' },
  { key: 'rate_extra_child', label: 'extra child', occupancy: 'EXTRA_CHILD' }
];

const WEEK_DAYS = [
  { key: 'MON', label: 'Mon', jsDay: 1 },
  { key: 'TUE', label: 'Tue', jsDay: 2 },
  { key: 'WED', label: 'Wed', jsDay: 3 },
  { key: 'THU', label: 'Thu', jsDay: 4 },
  { key: 'FRI', label: 'Fri', jsDay: 5 },
  { key: 'SAT', label: 'Sat', jsDay: 6 },
  { key: 'SUN', label: 'Sun', jsDay: 0 }
];

const toLocalDateOnly = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseInputValue = (raw) => {
  const str = String(raw ?? '').trim();
  if (!str) return '';
  const n = Number(str);
  if (!Number.isFinite(n) || n < 0) return '';
  return String(Number(n.toFixed(2)));
};

const valueToNumber = (raw) => {
  const n = Number(String(raw ?? '').trim());
  if (!Number.isFinite(n) || n < 0) return 0;
  return Number(n.toFixed(2));
};

const normalizeForCompare = (raw) => {
  const str = String(raw ?? '').trim();
  if (!str) return '';
  const n = Number(str);
  if (!Number.isFinite(n)) return '';
  return String(Number(n.toFixed(2)));
};

const getMatchingWeekdayDates = (startDate, endDate, targetDow) => {
  if (!startDate || !endDate || targetDow == null) return [];
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];

  const dates = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    if (cursor.getDay() === targetDow) dates.push(toLocalDateOnly(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

function PmsRateMatrixUpdate({ token, selectedProperty, onBackToView }) {
  const [ratePlans, setRatePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [values, setValues] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [selectedCell, setSelectedCell] = useState(null);
  const [dragState, setDragState] = useState({
    active: false,
    sourceValue: '',
    originOccKey: null,
    originDayKey: null
  });

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const cellKey = (occKey, dayKey) => `${occKey}|${dayKey}`;

  const selectedPlan = useMemo(
    () => ratePlans.find((rp) => String(rp.rate_plan_id) === String(selectedPlanId)) || null,
    [ratePlans, selectedPlanId]
  );

  const weekdayImpactPreview = useMemo(() => {
    if (!selectedPlan) return [];
    const stayStart = String(selectedPlan.stay_start_date || '').slice(0, 10);
    const stayEnd = String(selectedPlan.stay_end_date || '').slice(0, 10);
    if (!stayStart || !stayEnd) return [];

    return WEEK_DAYS.map((day) => {
      const datesCount = getMatchingWeekdayDates(stayStart, stayEnd, day.jsDay).length;
      const enteredRows = OCC_COLS.reduce((sum, occ) => {
        const key = cellKey(occ.key, day.key);
        return normalizeForCompare(values[key]) ? sum + 1 : sum;
      }, 0);
      return {
        dayKey: day.key,
        dayLabel: day.label,
        datesCount,
        enteredRows,
        totalUpdates: datesCount * enteredRows
      };
    }).filter((item) => item.enteredRows > 0);
  }, [selectedPlan, values]);

  const loadPlans = async () => {
    if (!selectedProperty?.id) {
      setRatePlans([]);
      setSelectedPlanId('');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/pms/rate-plans', { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || 'Failed to load rate plans');

      const plans = Array.isArray(data) ? data.filter((rp) => rp.is_active !== false) : [];
      setRatePlans(plans);
      if (!plans.length) {
        setSelectedPlanId('');
        return;
      }
      const exists = plans.some((rp) => String(rp.rate_plan_id) === String(selectedPlanId));
      if (!exists) setSelectedPlanId(String(plans[0].rate_plan_id));
    } catch (err) {
      setError(err?.message || 'Failed to load rate plans');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => {
    const next = {};
    OCC_COLS.forEach((col) => {
      WEEK_DAYS.forEach((day) => {
        next[cellKey(col.key, day.key)] = '';
      });
    });
    setValues(next);
    setInitialValues(next);
    setSelectedCell(null);
    setDragState({ active: false, sourceValue: '', originOccKey: null, originDayKey: null });
    setMessage('');
  }, [selectedPlanId]);

  useEffect(() => {
    const onMouseUp = () => setDragState((prev) => ({ ...prev, active: false }));
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  const setCellValue = (occKey, dayKey, rawValue) => {
    const normalized = parseInputValue(rawValue);
    const key = cellKey(occKey, dayKey);
    setValues((prev) => ({ ...prev, [key]: normalized }));
  };

  const onCellMouseDown = (occKey, dayKey) => {
    setSelectedCell({ occKey, dayKey });
  };

  const onFillHandleMouseDown = (e, occKey, dayKey) => {
    e.preventDefault();
    e.stopPropagation();
    const key = cellKey(occKey, dayKey);
    const sourceValue = String(values[key] ?? '');
    setSelectedCell({ occKey, dayKey });
    setDragState({
      active: true,
      sourceValue,
      originOccKey: occKey,
      originDayKey: dayKey
    });
  };

  const onCellMouseEnter = (occKey, dayKey) => {
    if (!dragState.active) return;

    const originOccIndex = OCC_COLS.findIndex((c) => c.key === dragState.originOccKey);
    const targetOccIndex = OCC_COLS.findIndex((c) => c.key === occKey);
    const originDayIndex = WEEK_DAYS.findIndex((d) => d.key === dragState.originDayKey);
    const targetDayIndex = WEEK_DAYS.findIndex((d) => d.key === dayKey);
    if (originOccIndex < 0 || targetOccIndex < 0 || originDayIndex < 0 || targetDayIndex < 0) return;

    const minOcc = Math.min(originOccIndex, targetOccIndex);
    const maxOcc = Math.max(originOccIndex, targetOccIndex);
    const minDay = Math.min(originDayIndex, targetDayIndex);
    const maxDay = Math.max(originDayIndex, targetDayIndex);

    setValues((prev) => {
      const next = { ...prev };
      for (let oi = minOcc; oi <= maxOcc; oi += 1) {
        const occMeta = OCC_COLS[oi];
        for (let di = minDay; di <= maxDay; di += 1) {
          const dayMeta = WEEK_DAYS[di];
          next[cellKey(occMeta.key, dayMeta.key)] = parseInputValue(dragState.sourceValue);
        }
      }
      return next;
    });
  };

  const saveChanges = async () => {
    if (!selectedProperty?.id) return;
    if (!selectedPlan) {
      setMessage('Select a rate plan first');
      return;
    }

    const changedKeys = Object.keys(values).filter((key) => normalizeForCompare(values[key]) !== normalizeForCompare(initialValues[key]));
    if (!changedKeys.length) {
      setMessage('No changes to save');
      return;
    }

    const stayStart = String(selectedPlan.stay_start_date || '').slice(0, 10);
    const stayEnd = String(selectedPlan.stay_end_date || '').slice(0, 10);
    if (!stayStart || !stayEnd) {
      setError('Selected rate plan must have stay start and end dates');
      return;
    }

    // Build compact weekday_rates — backend expands to matching dates in the stay period.
    const weekdayRates = {};
    changedKeys.forEach((key) => {
      const [occKey, dayKey] = key.split('|');
      const occMeta = OCC_COLS.find((c) => c.key === occKey);
      const dayMeta = WEEK_DAYS.find((d) => d.key === dayKey);
      if (!occMeta || !dayMeta) return;
      const raw = String(values[key] ?? '').trim();
      if (!raw) return;
      if (!weekdayRates[dayMeta.key]) weekdayRates[dayMeta.key] = {};
      weekdayRates[dayMeta.key][occMeta.occupancy] = valueToNumber(raw);
    });

    if (!Object.keys(weekdayRates).length) {
      setMessage('No valid weekday values to update in selected stay period');
      return;
    }

    const payload = {
      meal_plan: String(selectedPlan.meal_plan || 'EP').toUpperCase(),
      weekday_rates: weekdayRates
    };

    setSaveBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/pms/rate-plans/${encodeURIComponent(selectedPlan.rate_plan_id)}/calendar-rates-weekday`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save update');
      setMessage('Rate matrix updated');
      setInitialValues({ ...values });
    } catch (err) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - Update Rate Matrix</h2>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        <strong>Filters</strong>
        <select value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)} disabled={busy || saveBusy}>
          {!ratePlans.length ? <option value="">No Rate Plan</option> : null}
          {ratePlans.map((rp) => (
            <option key={rp.rate_plan_id} value={rp.rate_plan_id}>{rp.rate_plan_code} - {rp.rate_plan_name}</option>
          ))}
        </select>
        <button onClick={saveChanges} disabled={busy || saveBusy || !selectedPlan}>{saveBusy ? 'Updating...' : 'Save Update'}</button>
        <button onClick={onBackToView} disabled={busy || saveBusy}>RATE MATRIX VIEW</button>
        {selectedPlan ? (
          <span style={{ color: '#4a5568', fontSize: '0.85rem' }}>
            Stay: {fmtDate(String(selectedPlan.stay_start_date || '').slice(0, 10))} - {fmtDate(String(selectedPlan.stay_end_date || '').slice(0, 10))}
          </span>
        ) : null}
      </div>

      {selectedPlan && weekdayImpactPreview.length ? (
        <div style={{ marginBottom: '0.55rem', fontSize: '0.82rem', color: '#2d3748', background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.4rem 0.55rem' }}>
          {weekdayImpactPreview.map((item) => (
            <span key={item.dayKey} style={{ marginRight: '0.85rem', whiteSpace: 'nowrap' }}>
              <strong>{item.dayLabel}</strong>: {item.datesCount} dates, {item.enteredRows} rows, {item.totalUpdates} updates
            </span>
          ))}
        </div>
      ) : null}

      {!selectedPlan ? (
        <div className="card" style={{ padding: '0.75rem', color: '#718096' }}>
          {busy ? 'Loading rate plans...' : 'Select a rate plan to update'}
        </div>
      ) : (
        <div className="card" style={{ marginBottom: '0.8rem', overflowX: 'auto' }}>
          <div style={{ padding: '0.5rem 0.65rem', borderBottom: '1px solid var(--theme-border)', background: 'var(--theme-bg-light)', color: '#2d3748', fontSize: '0.86rem' }}>
            <strong>{selectedPlan.rate_plan_code}</strong> - {selectedPlan.rate_plan_name} | Room Type: {selectedPlan.room_type_name || '-'} | Meal Plan: {selectedPlan.meal_plan || 'EP'}
          </div>

          <table style={{ width: '100%', minWidth: `${Math.max(980, 220 + (WEEK_DAYS.length * 120))}px`, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--theme-bg-light)' }}>
                <th style={{ border: '1px solid #d9e2ec', padding: '0.45rem', textAlign: 'left', minWidth: '200px' }}>Occupancy</th>
                {WEEK_DAYS.map((day) => (
                  <th key={day.key} style={{ border: '1px solid #d9e2ec', padding: '0.45rem', textAlign: 'right', minWidth: '120px' }}>
                    {day.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {OCC_COLS.map((col) => (
                <tr key={col.key}>
                  <td style={{ border: '1px solid var(--theme-border)', padding: '0.4rem', fontWeight: 600, textTransform: 'capitalize' }}>{col.label}</td>
                  {WEEK_DAYS.map((day) => {
                    const key = cellKey(col.key, day.key);
                    const isSelected = selectedCell && selectedCell.occKey === col.key && selectedCell.dayKey === day.key;
                    return (
                      <td
                        key={`${col.key}-${day.key}`}
                        style={{ border: '1px solid var(--theme-border)', padding: '0.25rem', background: isSelected ? '#ebf8ff' : '#fff', position: 'relative' }}
                        onMouseEnter={() => onCellMouseEnter(col.key, day.key)}
                      >
                        <input
                          type="text"
                          inputMode="decimal"
                          value={String(values[key] ?? '')}
                          disabled={busy || saveBusy}
                          onChange={(e) => setCellValue(col.key, day.key, e.target.value)}
                          onMouseDown={() => onCellMouseDown(col.key, day.key)}
                          onFocus={() => setSelectedCell({ occKey: col.key, dayKey: day.key })}
                          style={{ width: '100%', textAlign: 'right', border: '1px solid #cbd5e0', borderRadius: '4px', padding: '0.25rem 0.35rem', background: '#fff' }}
                        />
                        {isSelected ? (
                          <button
                            type="button"
                            aria-label="Fill handle"
                            title="Drag to fill"
                            onMouseDown={(e) => onFillHandleMouseDown(e, col.key, day.key)}
                            style={{
                              position: 'absolute',
                              right: '2px',
                              bottom: '2px',
                              width: '12px',
                              height: '12px',
                              border: '1px solid var(--theme-primary)',
                              background: '#fff',
                              color: 'var(--theme-primary)',
                              borderRadius: '2px',
                              fontSize: '10px',
                              lineHeight: '10px',
                              padding: 0,
                              cursor: 'crosshair'
                            }}
                          >
                            +
                          </button>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '0.45rem', color: '#4a5568', fontSize: '0.82rem' }}>
        Update mode: enter weekday-wise values for selected rate plan and save. If a weekday does not occur in the selected stay period, nothing is updated for that weekday. Excel behavior: select a cell and drag from the + handle to fill across rows/columns.
      </div>
    </div>
  );
}

export default PmsRateMatrixUpdate;
